/**
 * LeadResearchTab - Research dashboard tab panel for a lead.
 * Extracted verbatim from LeadDetailPage.jsx (behavior unchanged); state/handlers passed as props.
 */
import React from 'react';
import { format, parseISO } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2, Clock, Cpu, Search, Eye, Sparkles, RefreshCw, Linkedin, Phone, Building2, Ban, Check, X, Copy, ChevronRight } from 'lucide-react';

export default function LeadResearchTab({
  research,
  researching,
  lead,
  researchElapsed,
  researchProgress,
  researchMsgIndex,
  RESEARCH_MESSAGES,
  showFullReport,
  setShowFullReport,
  handleResearch,
  handleCancelResearch,
}) {
  return (
                  researching && research?.status === 'in_progress' ? (
                    <div className="text-center py-12">
                      <style>{`
                        @keyframes fadeSlideIn {
                          0% { opacity: 0; transform: translateY(10px); }
                          100% { opacity: 1; transform: translateY(0); }
                        }
                        @keyframes pulseGlow {
                          0%, 100% { box-shadow: 0 0 8px rgba(147, 51, 234, 0.3); }
                          50% { box-shadow: 0 0 20px rgba(147, 51, 234, 0.6); }
                        }
                      `}</style>
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4" style={{ animation: 'pulseGlow 2s ease-in-out infinite' }}>
                        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                      </div>
                      <p className="text-slate-700 font-semibold text-lg mb-1">Researching {lead.first_name} {lead.last_name}</p>

                      {/* Elapsed timer */}
                      <div className="inline-flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1 mb-4">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span className="text-xs font-mono text-slate-500">
                          {Math.floor(researchElapsed / 60)}:{String(researchElapsed % 60).padStart(2, '0')}
                        </span>
                      </div>

                      {/* Current step indicator */}
                      {researchProgress.step && (
                        <div className="mb-3 max-w-md mx-auto">
                          <div className="flex items-center justify-center gap-2 mb-1.5">
                            {researchProgress.step === 'starting' && <Cpu className="h-4 w-4 text-purple-400 animate-pulse" />}
                            {researchProgress.step === 'searching' && <Search className="h-4 w-4 text-blue-500 animate-pulse" />}
                            {researchProgress.step === 'scraping' && <Eye className="h-4 w-4 text-emerald-500 animate-pulse" />}
                            {researchProgress.step === 'analyzing' && <Sparkles className="h-4 w-4 text-amber-500 animate-pulse" />}
                            <span className="text-sm font-medium text-slate-600 capitalize">{researchProgress.step}</span>
                          </div>
                          {researchProgress.detail && (
                            <p key={researchProgress.detail} className="text-xs text-slate-400 truncate" style={{ animation: 'fadeSlideIn 0.4s ease-out' }}>
                              {researchProgress.detail}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Progress steps bar */}
                      <div className="flex items-center gap-1 justify-center mb-4">
                        {['starting', 'searching', 'scraping', 'analyzing'].map((s) => {
                          const steps = ['starting', 'searching', 'scraping', 'analyzing'];
                          const currentIdx = steps.indexOf(researchProgress.step);
                          const stepIdx = steps.indexOf(s);
                          const isDone = stepIdx < currentIdx;
                          const isCurrent = s === researchProgress.step;
                          return (
                            <div key={s} className="flex items-center gap-1">
                              <div className={`h-1.5 w-8 rounded-full transition-all duration-500 ${isDone ? 'bg-purple-500' : isCurrent ? 'bg-purple-400 animate-pulse' : 'bg-slate-200'}`} />
                            </div>
                          );
                        })}
                      </div>

                      {/* Funny message */}
                      <p key={researchMsgIndex} className="text-sm text-purple-500 font-medium" style={{ animation: 'fadeSlideIn 0.5s ease-out' }}>
                        {RESEARCH_MESSAGES[researchMsgIndex]}
                      </p>

                      <button onClick={handleCancelResearch} className="mt-5 text-xs text-slate-400 hover:text-red-500 transition-colors underline underline-offset-2">
                        Cancel research
                      </button>
                    </div>
                  ) : (research?.profile || research?.report) ? (() => {
                    const p = research.profile || {};
                    // Old markdown format — prompt to re-run for dashboard view
                    if (!research.profile && research.report) {
                      return (
                        <div className="text-center py-10 space-y-3">
                          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-2">
                            <RefreshCw className="h-6 w-6 text-purple-500" />
                          </div>
                          <p className="text-sm font-medium text-slate-700">This research uses an older format</p>
                          <p className="text-xs text-slate-400">Re-run to get the new dashboard view with DISC profile, sales intel, and more.</p>
                          <Button onClick={handleResearch} disabled={researching} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8 px-4">
                            {researching ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Re-run Research
                          </Button>
                          <details className="text-left mt-4 max-w-2xl mx-auto">
                            <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">View old report</summary>
                            <div className="mt-2 prose prose-sm max-w-none text-slate-600 text-xs border rounded-lg p-3 max-h-60 overflow-y-auto">
                              <ReactMarkdown components={{ a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a> }}>
                                {research.report}
                              </ReactMarkdown>
                            </div>
                          </details>
                        </div>
                      );
                    }
                    const disc = p.personality || {};
                    const co = p.company || {};
                    const sa = p.sales_approach || {};
                    const discColors = { D: 'bg-red-500', I: 'bg-yellow-500', S: 'bg-green-500', C: 'bg-blue-500' };
                    const discBg = { D: 'bg-red-50 border-red-200', I: 'bg-yellow-50 border-yellow-200', S: 'bg-green-50 border-green-200', C: 'bg-blue-50 border-blue-200' };
                    const discText = { D: 'text-red-700', I: 'text-yellow-700', S: 'text-green-700', C: 'text-blue-700' };
                    // Truncate helper — first sentence or N chars
                    const short = (text, max = 60) => {
                      if (!text) return '';
                      const firstSentence = text.split(/[.!]\s/)[0];
                      if (firstSentence.length <= max) return firstSentence;
                      return text.slice(0, max).replace(/\s+\S*$/, '') + '...';
                    };
                    return (
                      <div className="space-y-3">
                        {/* Header row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-400">{research.search_count || 0} sources &middot; {research.pages_scraped || 0} pages</span>
                            {(research.found_linkedin || lead.linkedin_url) && (
                              <a href={research.found_linkedin || lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#0A66C2] hover:text-[#004182]" title="LinkedIn">
                                <Linkedin className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{research.researched_at ? format(parseISO(research.researched_at), 'MMM d, yyyy') : ''}</span>
                            <Button variant="outline" size="sm" onClick={handleResearch} disabled={researching} className="text-purple-600 border-purple-200 hover:bg-purple-50 h-7 text-xs">
                              {researching ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Refresh
                            </Button>
                          </div>
                        </div>

                        {/* === HERO CARD — Photo + DISC + Role + One-liner === */}
                        <div className="rounded-xl bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 border border-purple-200 p-4">
                          <div className="flex items-center gap-4">
                            {(lead.photo_url || research.photo_url) && (
                              <div className="shrink-0">
                                <img src={lead.photo_url || research.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg" onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                            )}
                            {disc.disc_type && (
                              <div className="shrink-0 text-center">
                                <div className={`w-14 h-14 rounded-full ${discColors[disc.disc_type] || 'bg-slate-400'} flex items-center justify-center text-white text-2xl font-bold shadow-lg`}>
                                  {disc.disc_type}
                                </div>
                                <span className={`text-[10px] font-bold mt-0.5 block ${discText[disc.disc_type] || 'text-slate-700'}`}>{disc.disc_label || disc.disc_type}</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              {p.professional?.current_role && <p className="text-sm font-semibold text-slate-800 leading-tight">{p.professional.current_role}</p>}
                              {co.industry && <p className="text-[11px] text-slate-500 mt-0.5">{co.industry}{co.size ? ` · ${co.size}` : ''}{co.headquarters ? ` · ${co.headquarters}` : ''}</p>}
                              {(disc.traits || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {disc.traits.slice(0, 4).map((t, i) => (
                                    <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-full ${discBg[disc.disc_type] || 'bg-slate-100'} ${discText[disc.disc_type] || 'text-slate-600'} font-medium border`}>{t}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* === KPI CARDS — 4 columns, icon + label + short value === */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {sa.best_channel && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-0.5"><Phone className="h-3 w-3 text-emerald-500" /><span className="text-[10px] font-semibold text-emerald-700 uppercase">Channel</span></div>
                              <p className="text-xs text-slate-700 leading-tight">{short(sa.best_channel, 50)}</p>
                            </div>
                          )}
                          {sa.timing && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-0.5"><Clock className="h-3 w-3 text-amber-500" /><span className="text-[10px] font-semibold text-amber-700 uppercase">Timing</span></div>
                              <p className="text-xs text-slate-700 leading-tight">{short(sa.timing, 50)}</p>
                            </div>
                          )}
                          {disc.decision_style && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-0.5"><Cpu className="h-3 w-3 text-blue-500" /><span className="text-[10px] font-semibold text-blue-700 uppercase">Decisions</span></div>
                              <p className="text-xs text-slate-700 leading-tight">{short(disc.decision_style, 50)}</p>
                            </div>
                          )}
                          {co.revenue && (
                            <div className="bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-0.5"><Building2 className="h-3 w-3 text-violet-500" /><span className="text-[10px] font-semibold text-violet-700 uppercase">Revenue</span></div>
                              <p className="text-xs text-slate-700 leading-tight">{co.revenue}</p>
                            </div>
                          )}
                        </div>

                        {/* === RED FLAGS — only if present === */}
                        {(p.red_flags || []).length > 0 && (
                          <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                            <Ban className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                            <div className="flex flex-wrap gap-1">
                              {p.red_flags.map((f, i) => (
                                <span key={i} className="text-[11px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{f}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* === DO / DON'T — compact 2-col === */}
                        {((disc.communication_dos || []).length > 0 || (disc.communication_donts || []).length > 0) && (
                          <div className="grid grid-cols-2 gap-2">
                            {(disc.communication_dos || []).length > 0 && (
                              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                                <span className="text-[10px] font-bold text-green-700 uppercase">Do</span>
                                {disc.communication_dos.slice(0, 3).map((d, i) => (
                                  <div key={i} className="flex items-start gap-1 mt-1"><Check className="h-3 w-3 text-green-500 shrink-0 mt-0.5" /><span className="text-[11px] text-slate-700 leading-tight">{d}</span></div>
                                ))}
                              </div>
                            )}
                            {(disc.communication_donts || []).length > 0 && (
                              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                <span className="text-[10px] font-bold text-red-700 uppercase">Don't</span>
                                {disc.communication_donts.slice(0, 3).map((d, i) => (
                                  <div key={i} className="flex items-start gap-1 mt-1"><X className="h-3 w-3 text-red-400 shrink-0 mt-0.5" /><span className="text-[11px] text-slate-700 leading-tight">{d}</span></div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* === OPENER — single best one === */}
                        {(sa.opening_lines || []).length > 0 && (
                          <div className="bg-white border border-purple-200 rounded-lg p-2.5 flex items-start gap-2.5 group">
                            <Sparkles className="h-4 w-4 text-purple-400 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <span className="text-[10px] font-bold text-purple-600 uppercase">Opener</span>
                              <p className="text-xs text-slate-700 italic mt-0.5">&ldquo;{sa.opening_lines[0]}&rdquo;</p>
                            </div>
                            <button onClick={() => { navigator.clipboard.writeText(sa.opening_lines[0]); toast.success('Copied!'); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-purple-50 rounded transition-opacity" title="Copy"><Copy className="h-3.5 w-3.5 text-purple-400" /></button>
                          </div>
                        )}

                        {/* === PAIN POINTS as pills === */}
                        {(sa.pain_points || []).length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-[10px] font-bold text-slate-500 uppercase shrink-0 mt-1">Pain Points</span>
                            <div className="flex flex-wrap gap-1">
                              {sa.pain_points.map((pp, i) => (
                                <span key={i} className="text-[11px] bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full">{pp}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* === EXPERTISE as pills === */}
                        {(p.professional?.expertise || []).length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-[10px] font-bold text-slate-500 uppercase shrink-0 mt-1">Expertise</span>
                            <div className="flex flex-wrap gap-1">
                              {p.professional.expertise.map((e, i) => (
                                <span key={i} className="text-[11px] bg-indigo-50 text-indigo-600 border border-indigo-200 px-2 py-0.5 rounded-full">{e}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* === FULL REPORT TOGGLE === */}
                        <button onClick={() => setShowFullReport(!showFullReport)} className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 py-2 border border-dashed border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
                          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showFullReport ? 'rotate-90' : ''}`} />
                          {showFullReport ? 'Hide full report' : 'Show full report'}
                        </button>

                        {showFullReport && (
                          <div className="space-y-3 pt-1">
                            {/* Summary */}
                            {p.summary && (
                              <div className="bg-slate-50 border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Summary</span>
                                <p className="text-xs text-slate-700 mt-1">{p.summary}</p>
                              </div>
                            )}

                            {/* Sales details */}
                            {(sa.value_prop || disc.email_style || disc.call_approach) && (
                              <div className="border rounded-lg p-3 space-y-2">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Sales Intel</span>
                                {sa.value_prop && <div className="mt-1"><span className="text-[10px] font-semibold text-slate-400">Value Prop</span><p className="text-xs text-slate-700">{sa.value_prop}</p></div>}
                                {disc.email_style && <div><span className="text-[10px] font-semibold text-slate-400">Email Style</span><p className="text-xs text-slate-700">{disc.email_style}</p></div>}
                                {disc.call_approach && <div><span className="text-[10px] font-semibold text-slate-400">Call Approach</span><p className="text-xs text-slate-700">{disc.call_approach}</p></div>}
                              </div>
                            )}

                            {/* More openers */}
                            {(sa.opening_lines || []).length > 1 && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">More Openers</span>
                                <div className="mt-1 space-y-1.5">{sa.opening_lines.slice(1).map((line, i) => (
                                  <div key={i} className="flex items-start gap-2 group">
                                    <span className="text-[10px] text-slate-400 mt-0.5 shrink-0">{i+2}.</span>
                                    <p className="text-xs text-slate-700 flex-1 italic">&ldquo;{line}&rdquo;</p>
                                    <button onClick={() => { navigator.clipboard.writeText(line); toast.success('Copied!'); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-100 rounded" title="Copy"><Copy className="h-3 w-3 text-slate-400" /></button>
                                  </div>
                                ))}</div>
                              </div>
                            )}

                            {/* Objections */}
                            {(sa.objections || []).length > 0 && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Objection Handling</span>
                                <div className="mt-1 space-y-2">{sa.objections.map((obj, i) => (
                                  <div key={i}><p className="text-xs text-slate-700 font-medium">&ldquo;{obj.objection}&rdquo;</p><p className="text-[11px] text-slate-500 mt-0.5">{obj.response}</p></div>
                                ))}</div>
                              </div>
                            )}

                            {/* Company */}
                            {(co.description || co.hiring_activity) && (
                              <div className="border rounded-lg p-3 space-y-1.5">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Company{co.industry ? `: ${co.industry}` : ''}</span>
                                {co.description && <p className="text-xs text-slate-700">{co.description}</p>}
                                <div className="grid grid-cols-3 gap-2 text-[11px]">
                                  {co.size && <div><span className="text-slate-400 block">Size</span><span className="text-slate-700">{co.size}</span></div>}
                                  {co.revenue && <div><span className="text-slate-400 block">Revenue</span><span className="text-slate-700">{co.revenue}</span></div>}
                                  {co.headquarters && <div><span className="text-slate-400 block">HQ</span><span className="text-slate-700">{co.headquarters}</span></div>}
                                </div>
                                {co.hiring_activity && <div><span className="text-[10px] font-semibold text-slate-400">Hiring</span><p className="text-xs text-slate-600">{co.hiring_activity}</p></div>}
                                {(co.competitors || []).length > 0 && <p className="text-[11px] text-slate-500">Competitors: {co.competitors.join(', ')}</p>}
                              </div>
                            )}

                            {/* Career */}
                            {(p.professional?.career_history || p.professional?.education) && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Career</span>
                                {p.professional.career_history && <p className="text-xs text-slate-700 mt-1">{p.professional.career_history}</p>}
                                {p.professional.education && <p className="text-[11px] text-slate-500 mt-1">{p.professional.education}</p>}
                              </div>
                            )}

                            {/* Content / Speaking / News */}
                            {((p.content || []).length > 0 || (p.speaking || []).length > 0 || (p.news || []).length > 0) && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Content & Media</span>
                                <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                                  {(p.content || []).length > 0 && <div>{p.content.slice(0, 3).map((c, i) => <div key={i} className="mt-0.5">{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-indigo-600 hover:underline">{c.title}</a> : <span className="text-[11px] text-slate-700">{c.title}</span>}</div>)}</div>}
                                  {(p.speaking || []).length > 0 && <div>{p.speaking.slice(0, 3).map((s, i) => <div key={i} className="mt-0.5"><span className="text-[11px] text-slate-700">{s.event}</span></div>)}</div>}
                                  {(p.news || []).length > 0 && <div>{p.news.slice(0, 3).map((n, i) => <div key={i} className="mt-0.5">{n.url ? <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-indigo-600 hover:underline">{n.headline}</a> : <span className="text-[11px] text-slate-700">{n.headline}</span>}</div>)}</div>}
                                </div>
                              </div>
                            )}

                            {/* Key Connections */}
                            {(p.key_connections || []).length > 0 && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Key Connections</span>
                                <div className="flex flex-wrap gap-1 mt-1">{p.key_connections.map((c, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{c}</span>)}</div>
                              </div>
                            )}

                            {/* Sources */}
                            {(research.citations || []).length > 0 && (
                              <details className="text-xs">
                                <summary className="text-slate-400 cursor-pointer hover:text-slate-600">{research.citations.length} sources</summary>
                                <div className="mt-2 space-y-0.5 pl-2">
                                  {research.citations.map((c, i) => (
                                    <a key={i} href={c.url} target="_blank" rel="noopener noreferrer" className="block text-indigo-600 hover:underline truncate text-[11px]">{c.title || c.url}</a>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })() : research ? (
                    <div className="text-center py-10 space-y-3">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 mb-2">
                        <Search className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-sm font-medium text-slate-700">
                        {research.status === 'failed' ? 'Research failed' : 'No research data available'}
                      </p>
                      {research.error && <p className="text-xs text-red-500">{research.error}</p>}
                      <Button onClick={handleResearch} disabled={researching} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8 px-4">
                        {researching ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Run Research
                      </Button>
                    </div>
                  ) : null
  );
}
